import type { FC } from 'react';
import { memo } from 'react';
import { Box, Chip, Dialog, Divider, IconButton, Tooltip, Typography } from '@mui/material';
import { Close, OpenInNew } from '@mui/icons-material';
import { CreativeAppName, CreativePaper } from 'features/creatives';
import type { GoogleCreative } from '../api/types';
import GoogleGeoPanel from './GoogleGeoPanel';
import GoogleCreativeVariationsCarousel from './GoogleCreativeVariationsCarousel';

interface GoogleExpandedCreativeDialogProps {
  open: boolean;
  onClose: () => void;
  creative: GoogleCreative;
}

interface MetricChipsProps {
  winnerConfidencePercent: number;
  longevity: number;
  variationsCount: number;
}

const MetricChips: FC<MetricChipsProps> = ({ winnerConfidencePercent, longevity, variationsCount }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
    <Tooltip title="Winner confidence score">
      <Chip
        size="small"
        label={`${winnerConfidencePercent}%`}
        sx={{ backgroundColor: '#E8F5E9', color: '#2E7D32', fontWeight: 600, borderRadius: 1 }}
      />
    </Tooltip>
    <Tooltip title="Longevity (days active)">
      <Chip
        size="small"
        label={`${longevity}d`}
        sx={{ backgroundColor: '#E3F2FD', color: '#1565C0', fontWeight: 600, borderRadius: 1 }}
      />
    </Tooltip>
    <Tooltip title="Number of variations">
      <Chip
        size="small"
        label={`${variationsCount} var.`}
        sx={{ backgroundColor: '#F3E5F5', color: '#6A1B9A', fontWeight: 600, borderRadius: 1 }}
      />
    </Tooltip>
  </Box>
);

interface DialogSidebarProps {
  creative: GoogleCreative;
}

const DialogSidebar: FC<DialogSidebarProps> = ({ creative }) => {
  const winnerConfidencePercent = Math.round(creative.winnerConfidenceScore * 100);
  const primaryDomain = creative.domains[0];
  const companyName = creative.company || creative.advertiserName || 'Unknown company';

  return (
    <Box
      sx={{
        width: 320,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        borderLeft: 1,
        borderColor: 'divider',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          p: 2.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          overflowY: 'auto',
          flexGrow: 1,
        }}
      >
        {/* Company + metrics */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <Tooltip title={companyName}>
            <CreativeAppName sx={{ fontSize: '0.9375rem' }}>
              {companyName}
            </CreativeAppName>
          </Tooltip>
          <MetricChips
            winnerConfidencePercent={winnerConfidencePercent}
            longevity={creative.longevity}
            variationsCount={creative.variationsCount}
          />
        </Box>

        {/* Domain */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <OpenInNew sx={{ fontSize: 14, color: 'text.disabled' }} />
          <Typography
            component="a"
            href={primaryDomain ? `https://${primaryDomain}` : undefined}
            target="_blank"
            rel="noopener noreferrer"
            variant="caption"
            sx={{
              color: 'text.secondary',
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline', color: 'primary.main' },
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {primaryDomain || 'Unknown domain'}
          </Typography>
        </Box>

        <Divider />

        {/* Geo panel */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Geographic reach
          </Typography>
          <GoogleGeoPanel regionStats={creative.regionStats} />
        </Box>
      </Box>
    </Box>
  );
};

const GoogleExpandedCreativeDialog: FC<GoogleExpandedCreativeDialogProps> = memo(
  ({ open, onClose, creative }: GoogleExpandedCreativeDialogProps) => {
    return (
      <Dialog
        maxWidth={false}
        PaperProps={{
          sx: {
            p: 0,
            width: '90%',
            maxWidth: 1100,
            height: '90vh',
            overflowY: 'visible',
          },
        }}
        transitionDuration={{ appear: 0, enter: 0, exit: 0 }}
        open={open}
        onClose={onClose}
      >
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            height: '100%',
            overflow: 'hidden',
            borderRadius: '8px',
          }}
        >
          {/* Left: creative preview */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              bgcolor: 'background.default',
              display: 'flex',
              flexDirection: 'column',
              p: 2,
            }}
          >
            <CreativePaper
              id={`google-ads-creative-${creative.adCreativeId}-expanded`}
              sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
            >
              <Box sx={{ position: 'relative', display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
                <GoogleCreativeVariationsCarousel
                  creative={creative}
                  containerId={`google-ads-creative-${creative.adCreativeId}-expanded`}
                />
              </Box>
            </CreativePaper>
          </Box>

          {/* Right: sidebar */}
          <DialogSidebar creative={creative} />
        </Box>

        {/* Close button */}
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: -12,
            right: -12,
            bgcolor: 'background.paper',
            boxShadow: 2,
            '&:hover': { bgcolor: 'grey.100' },
          }}
        >
          <Close color="action" />
        </IconButton>
      </Dialog>
    );
  }
);

GoogleExpandedCreativeDialog.displayName = 'GoogleExpandedCreativeDialog';
export default GoogleExpandedCreativeDialog;
